import {
  checkProposedDeadlineSatisfied,
  useformatDeadline,
} from "@/features/offer-cargo-destination-deadline";
import type { AdminDeliveryProposalDetailPageProps } from "@/pages/admin-delivery-proposal-detail/model";
import type {
  CreateDeliveryProposalFormSchemaType,
  DeliveryProposalDestinationDeadlineFormSchemaType,
} from "@/pages/admin-delivery-proposal-detail/model/validation";
import type { LoadableData } from "@/shared/lib/loadable";
import { BaseMessageCard } from "@/shared/ui/base-message-card";
import { BasePaper } from "@/shared/ui/base-paper";
import { BaseTextField } from "@/shared/ui/base-text-field";
import { DeliveryProposalFormTable } from "@/widgets/delivery-proposal-form-table";
import { MobileDeliveryProposalFormTable } from "@/widgets/delivery-proposal-form-table/mobile-delivery-proposal-form-table";
import { DeliveryRouteTaskContentTags } from "@/widgets/delivery-route-task-content-tags";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import React from "react";
import type { Control } from "react-hook-form";
import { DeadlineFormDialog } from "../deadline-form-dialog";

export type DestinationFormProps = {
  control: Control<CreateDeliveryProposalFormSchemaType>;
  taskContent: "loading" | "transit" | "unloading";
  draftOfferCargo: LoadableData<
    AdminDeliveryProposalDetailPageProps["draftOfferCargo"]
  >;
  draftOfferCargoDestination: LoadableData<
    AdminDeliveryProposalDetailPageProps["draftOfferCargo"]
  >["destinations"][0];
  currentDeadlineValues:
    | CreateDeliveryProposalFormSchemaType["loadingDestination"]
    | CreateDeliveryProposalFormSchemaType["unloadingDestination"];
  errors: {
    deadline: string | undefined;
  };
  onChange: (values: DeliveryProposalDestinationDeadlineFormSchemaType) => void;
};

export const DestinationForm = (props: DestinationFormProps) => {
  const theme = useTheme();
  const { currentDeadlineValues, onChange } = props;

  const [deadlineModalOpen, setDeadlineModalOpen] = React.useState(false);

  const title =
    props.taskContent === "loading"
      ? "【積地】"
      : props.taskContent === "unloading"
        ? "【卸地】"
        : "【経由地】";

  const draftDeadline = {
    deadlineDate:
      props.taskContent === "loading"
        ? props.draftOfferCargo.loadingOn
        : props.taskContent === "unloading"
          ? props.draftOfferCargo.unloadingOn
          : undefined,
    deadlineType: props.draftOfferCargoDestination.deadlineType,
    deadlineTime: props.draftOfferCargoDestination.deadlineTime,
    deadlineTimeEnd: props.draftOfferCargoDestination.deadlineTimeEnd,
  };
  const { deadlineText: draftDeadlineText } = useformatDeadline(draftDeadline);
  const { deadlineText: proposalDeadlineText } = useformatDeadline(
    currentDeadlineValues,
  );
  const isCurrentDeadlineSatisfied = currentDeadlineValues
    ? checkProposedDeadlineSatisfied(draftDeadline, currentDeadlineValues)
    : true;

  const deadlineCells = [
    draftDeadlineText,
    <Stack key={"deadline-proposal"} spacing={1}>
      <BaseTextField
        key="proposal"
        fullWidth
        value={proposalDeadlineText ?? undefined}
        error={!!props.errors.deadline}
        helperText={props.errors.deadline}
        onClick={() => {
          setDeadlineModalOpen(true);
        }}
        multiline
      />
      {!isCurrentDeadlineSatisfied && (
        <BaseMessageCard
          message="荷物の要件を満たしていません。"
          status="warning"
          size="small"
        />
      )}
    </Stack>,
  ];

  const addressCells = [
    `${props.draftOfferCargoDestination.prefecture} ${props.draftOfferCargoDestination.address}`,
    null,
  ];

  const taskDetailCells = [
    props.draftOfferCargoDestination.taskDetail || null,
    null,
  ];

  const memoCells = [props.draftOfferCargoDestination.memo, null];

  return (
    <BasePaper sx={{ p: 2, backgroundColor: theme.palette.grey[50] }}>
      <DeadlineFormDialog
        open={deadlineModalOpen}
        taskContent={props.taskContent}
        draftOfferCargo={props.draftOfferCargo}
        draftOfferCargoDestination={props.draftOfferCargoDestination}
        currentValues={currentDeadlineValues}
        onSubmit={(deadlineDate, destination) => {
          onChange({
            deadlineDate,
            ...destination,
          });
        }}
        onClose={() => {
          setDeadlineModalOpen(false);
        }}
      />
      <Stack spacing={2}>
        <Stack direction="row" spacing={0} alignItems={"center"}>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {title} 作業内容：
          </Typography>
          <DeliveryRouteTaskContentTags
            hasLoading={!!props.draftOfferCargoDestination.hasLoading}
            hasUnloading={!!props.draftOfferCargoDestination.hasUnloading}
          />
        </Stack>

        {/* PC向けのフォーム */}
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <DeliveryProposalFormTable
            headers={["荷物", "提案"]}
            rows={[
              {
                title:
                  props.taskContent === "loading"
                    ? "積日時/発日時"
                    : "卸日時/着日時",
                cells: deadlineCells,
              },
              {
                title:
                  props.taskContent === "loading" ? "積地/発地" : "卸地/着地",
                cells: addressCells,
              },
              {
                title: "作業詳細",
                cells: taskDetailCells,
              },
              {
                title: "特記事項(日付等)",
                cells: memoCells,
              },
            ]}
          />
        </Box>

        {/* スマホ向けのフォーム */}
        <Box sx={{ display: { xs: "block", sm: "none" } }}>
          <MobileDeliveryProposalFormTable
            items={[
              {
                label:
                  props.taskContent === "loading"
                    ? "積日時/発日時"
                    : "卸日時/着日時",
                cargo: deadlineCells[0],
                proposal: deadlineCells[1],
              },
              {
                label:
                  props.taskContent === "loading" ? "積地/発地" : "卸地/着地",
                cargo: addressCells[0],
                proposal: addressCells[1],
              },
              {
                label: "作業詳細",
                cargo: taskDetailCells[0],
                proposal: taskDetailCells[1],
              },
              {
                label: "特記事項(日付等)",
                cargo: memoCells[0],
                proposal: memoCells[1],
              },
            ]}
          />
        </Box>
      </Stack>
    </BasePaper>
  );
};
